import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Link as RouterLink, useLocation, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
  Autocomplete,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import SendIcon from "@mui/icons-material/Send";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import UndoIcon from "@mui/icons-material/Undo";
import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import LinkIcon from "@mui/icons-material/Link";
import { LeftSidebar as NavFrame } from "layout-blueprints";
import StandardHeader from "../../components/global/StandardHeader";
import { TYPE_COLOR, nextRunText } from "./outcomes";
import { useSearch } from "./NewMoveDialog";
import {
  MAX_USAGE_KWH,
  BillPanel,
  ChecksStrip,
  KeyValues,
  NotesPanel,
  Panel,
  UnitPanel,
  UnitTimeline,
  VerdictBar,
  billPeriod,
  buildBatchChecks,
  fromUtc,
  money,
  relativeDay,
  processedRivalText,
} from "./DetailParts";

function formatDate(value) {
  if (!value || value === "0000-00-00") return "—";
  const parsed = moment(value, "YYYY-MM-DD", true);
  return parsed.isValid() ? parsed.format("MMM D, YYYY") : value;
}

function formatDateTime(value) {
  const local = fromUtc(value);
  return local ? local.format("MMM D, YYYY h:mm A") : "—";
}

// Who created the move. SYSTEM is the creator column's default when no one was signed in: a move-in from the
// public website form, or a move-out from an internal workflow.
function creatorText(kind, name, raw) {
  if (name) return `by ${name}`;
  if (raw === "SYSTEM") return kind === "in" ? "on the website" : "automatically";
  if (raw === "LOCAL_SEED") return "by the local seed";
  if (/^\d+$/.test(String(raw || ""))) return `by user #${raw}`;
  return raw ? `by ${raw}` : "";
}

function joinAddress(parts) {
  const [line1, line2, city, state, zip] = parts;
  const street = [line1, line2].filter(Boolean).join(" ");
  const place = [city, [state, zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  return [street, place].filter(Boolean).join(", ") || null;
}

function accountHref(move) {
  if (!move.accountNum) return null;
  if (move.accountUuid) return `/accountmanager/${move.accountNum}/${move.accountUuid}`;
  return `/accountmanager/${move.accountNum}`;
}

function propertyHref(move) {
  if (!move.propertyUuid || !move.propertyId || !move.propertyManagerId) return null;
  return `/property/${move.propertyUuid}/${move.propertyId}/${move.propertyManagerId}`;
}

function unitHref(move) {
  if (!move.unitUuid || !move.unitId) return null;
  return `/unit/${move.unitUuid}/${move.unitId}`;
}

const CUSTOMER_FIELDS = {
  in: ["firstName", "lastName", "email", "phone1", "phone2", "billingAddress1", "billingAddress2", "billingCity", "billingState", "billingZip"],
  out: ["email", "phone", "fwdAddress1", "fwdAddress2", "fwdCity", "fwdState", "fwdZip"],
};
const billDeliveryOf = (move) => {
  const email = Number(move.eBills) === 1;
  const paper = Number(move.printedBills) === 1;
  return email && paper ? "both" : paper ? "paper" : "email";
};
const BILL_DELIVERY_FLAGS = { email: { eBills: 1, printedBills: 0 }, paper: { eBills: 0, printedBills: 1 }, both: { eBills: 1, printedBills: 1 } };

function recordState(move) {
  if (Number(move.deleted) === 1) return { label: "Deleted", color: "default" };
  if (Number(move.processed) === 1) return { label: "Processed", color: "success" };
  if (Number(move.issues) === 1) return { label: "Flagged", color: "error" };
  if (Number(move.onHold) === 1) return { label: "On hold", color: "warning" };
  if (Number(move.readyForProcess) === 1) return { label: "Queued", color: "info" };
  return { label: "Open", color: "default" };
}

function MoveDetailPage(props) {
  const navigate = useNavigate();
  const location = useLocation();
  const { kind, uuid } = useParams();
  const details = props.user?.details;
  const canView = Boolean(details && (details.billing || details.administration || details.isSuperUser));
  const validKind = kind === "in" || kind === "out";
  const [move, setMove] = useState(null);
  const [bill, setBill] = useState(null);
  const [occupantMoveOuts, setOccupantMoveOuts] = useState([]);
  const [emailMatches, setEmailMatches] = useState([]);
  const [hasPortalUser, setHasPortalUser] = useState(false);
  const [unitMoves, setUnitMoves] = useState([]);
  const [meterReads, setMeterReads] = useState([]);
  const [otherMeters, setOtherMeters] = useState([]);
  const [handoff, setHandoff] = useState(null);
  const [unitMeters, setUnitMeters] = useState([]);
  const [moveDateReads, setMoveDateReads] = useState([]);
  const [deletion, setDeletion] = useState(null);
  const [editOtherReads, setEditOtherReads] = useState({});
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [linkBillOpen, setLinkBillOpen] = useState(false);
  const [currentOccupant, setCurrentOccupant] = useState(null);
  // The batch's next run and APL's business date, from the server (2 AM and the calendar are Eastern).
  const [schedule, setSchedule] = useState({ nextRun: null, today: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editCustomer, setEditCustomer] = useState({});
  const [editUnit, setEditUnit] = useState(null);
  const [unitQuery, setUnitQuery] = useState("");
  const unitSearch = useSearch("/moves-dashboard/unit-search", unitQuery);
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [processNoBillDialogOpen, setProcessNoBillDialogOpen] = useState(false);
  const [finishBillOpen, setFinishBillOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [successInfo, setSuccessInfo] = useState(null);
  const [unitLastBill, setUnitLastBill] = useState(null);
  const [billingTarget, setBillingTarget] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [billing, setBilling] = useState(null);
  const [billPreview, setBillPreview] = useState(null);
  const [clearFlagOpen, setClearFlagOpen] = useState(false);
  const [clearFlagReason, setClearFlagReason] = useState("");
  const [clearFlagError, setClearFlagError] = useState("");
  const [moveBill, setMoveBill] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [creatingMoveOut, setCreatingMoveOut] = useState(false);
  // The date for "Create the tenant's move-out": the move-in date unless the analyst picks an earlier one.
  const [linkedMoveOutOpen, setLinkedMoveOutOpen] = useState(false);
  const [linkedMoveOutDate, setLinkedMoveOutDate] = useState("");

  const dateField = kind === "out" ? "moveOutDate" : "moveInDate";
  const dateLabel = kind === "out" ? "Move-out date" : "Move-in date";
  const [editMeterRead, setEditMeterRead] = useState("");
  const [editMoveDate, setEditMoveDate] = useState("");
  const [editReadAmtType, setEditReadAmtType] = useState("");

  const basePath = kind === "out" ? `/moves-dashboard/move-outs/${uuid}` : `/moves-dashboard/move-ins/${uuid}`;

  const applyDetail = (data) => {
    setMove(data?.move || null);
    setBill(data?.bill || null);
    setUnitLastBill(data?.unitLastBill || null);
    setBillingTarget(data?.billingTarget || null);
    setPrediction(data?.prediction || null);
    setBilling(data?.billing || null);
    setBillPreview(data?.billPreview || null);
    setMoveBill(data?.moveBill || null);
    setOccupantMoveOuts(data?.occupantMoveOuts || []);
    setEmailMatches(data?.emailMatches || []);
    setHasPortalUser(data?.hasPortalUser || false);
    setUnitMoves(data?.unitMoves || []);
    setMeterReads(data?.meterReads || []);
    setOtherMeters(data?.otherMeters || []);
    setHandoff(data?.handoff || null);
    setUnitMeters(data?.unitMeters || []);
    setMoveDateReads(data?.moveDateReads || []);
    setDeletion(data?.deletion || null);
    setCurrentOccupant(data?.currentOccupant || null);
    setSchedule({ nextRun: data?.nextRun || null, today: data?.today || null });
  };

  useEffect(() => {
    document.title = move?.name ? `Ubil+ ${move.name}` : "Ubil+ Move";
  }, [move]);

  // Set by the new-move dialog after it creates this move.
  useEffect(() => {
    const notice = location.state?.notice;
    if (notice) {
      setSnack({ open: true, message: notice.message, severity: notice.severity || "success" });
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.key]);

  useEffect(() => {
    if (!canView || !validKind || !uuid) return undefined;
    let active = true;
    setLoading(true);
    setError("");
    axios.get(basePath)
      .then((response) => { if (active) applyDetail(response.data); })
      .catch((err) => {
        if (!active) return;
        applyDetail(null);
        setError(err?.response?.data?.message || "That move could not be loaded.");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [canView, validKind, kind, uuid]);

  // The unit's other meters (a garage) and their reads on the move date: one provided for the move (actual, final),
  // or one from the meter readings, which the final bill uses as it is.
  const otherUnitMeters = unitMeters.slice(1);
  const readOnMoveDate = (meterId, provided) => moveDateReads.find((r) => String(r.meterId) === String(meterId)
    && r.readType === "ACT" && Number(r.billed) !== 1 && (Number(r.finalRead) === 1) === provided);
  const providedOtherReads = otherUnitMeters.map((m) => readOnMoveDate(m.meterId, true)).filter(Boolean);

  useEffect(() => {
    const reads = {};
    unitMeters.slice(1).forEach((m) => {
      const provided = moveDateReads.find((r) => String(r.meterId) === String(m.meterId) && r.readType === "ACT" && Number(r.finalRead) === 1 && Number(r.billed) !== 1);
      reads[m.meterId] = provided ? String(provided.readAmount) : "";
    });
    setEditOtherReads(reads);
  }, [unitMeters, moveDateReads]);

  useEffect(() => {
    if (!move) return;
    const values = {};
    (CUSTOMER_FIELDS[kind] || []).forEach((name) => { values[name] = move[name] == null ? "" : String(move[name]); });
    if (kind === "in") {
      values.billDelivery = billDeliveryOf(move);
      values.ssn = "";
    }
    setEditCustomer(values);
    setEditUnit(move.unitUuid ? { unitUuid: move.unitUuid, unitAddress: move.unitAddress, propertyName: move.propertyName } : null);
  }, [move, kind]);

  useEffect(() => {
    if (move) {
      setEditMeterRead(String(move.meterRead ?? ""));
      setEditMoveDate(move.moveDate ? moment(move.moveDate, "YYYY-MM-DD").format("YYYY-MM-DD") : "");
      setEditReadAmtType(move.readAmtType || "");
    }
  }, [move]);

  const reload = () => axios.get(basePath).then((response) => applyDetail(response.data));

  useEffect(() => {
    if (!billing || billing.status !== "queued") return undefined;
    const timer = setInterval(() => { reload().catch(() => {}); }, 5000);
    return () => clearInterval(timer);
  }, [billing?.status]);

  const moveIdOf = (m) => m?.id || m?.moveInId || m?.moveOutId;

  const fetchNotes = async (moveId) => {
    if (!moveId) return;
    setLoadingNotes(true);
    try {
      const table = kind === "out" ? "CustomerMoveOut" : "CustomerMoveIn";
      const response = await axios.get(`/moves-dashboard/notes?moveTable=${table}&moveId=${moveId}`);
      setNotes(response.data || []);
    } catch {
      setNotes([]);
    } finally {
      setLoadingNotes(false);
    }
  };

  useEffect(() => {
    const moveId = moveIdOf(move);
    if (moveId) fetchNotes(moveId);
  }, [move?.id, move?.moveInId, move?.moveOutId]);

  const saveNote = (text) => axios.post("/moves-dashboard/notes", {
    note: text,
    noteTopicId: 7,
    accountId: move.accountId || null,
    moveTable: kind === "out" ? "CustomerMoveOut" : "CustomerMoveIn",
    moveId: moveIdOf(move),
    priority: 1,
  });

  const addNote = async (text) => {
    try {
      await saveNote(text);
      fetchNotes(moveIdOf(move));
      return true;
    } catch (err) {
      setSnack({ open: true, message: err?.response?.data?.message || "Failed to add note.", severity: "error" });
      return false;
    }
  };

  const retryBilling = async () => {
    setSaving(true);
    try {
      await axios.post(`${basePath}/retry-billing`);
      await reload();
      setSnack({ open: true, message: "Billing queued again.", severity: "success" });
    } catch (err) {
      setSnack({ open: true, message: err?.response?.data?.message || "Couldn't retry billing.", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const finishBill = async () => {
    setSaving(true);
    try {
      const response = await axios.post(`${basePath}/finish-billing`);
      applyDetail(response.data.detail);
      setFinishBillOpen(false);
      const done = response.data.finished || {};
      const parts = ["Bill finished."];
      if (done.pdfs) parts.push(done.emailed ? "The PDF was made and emailed." : "The PDF was made.");
      if (done.emailFailed) parts.push("The email didn't go out, so send the PDF another way.");
      if (done.printed) parts.push("This account gets paper bills: print and mail it, since it wasn't added to a print run.");
      setSnack({ open: true, message: parts.join(" "), severity: done.emailFailed || done.printed ? "warning" : "success" });
    } catch (err) {
      setSnack({ open: true, message: err?.response?.data?.message || "Couldn't finish the bill.", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  // The old batch's second pass on a two-meter unit pointed the move at an empty estimate after it was billed.
  const linkBill = async () => {
    const found = billing?.unlinkedBill;
    if (!found) return;
    setSaving(true);
    try {
      await axios.post(`${basePath}/link-bill`, { workTableUuid: found.workTableUuid });
      await saveNote(`Linked the final bill made when this move was processed: ${money(found.newCharges)} in new charges to ${found.name || "the account"} (#${found.accountNum}). The move had pointed to an empty estimate left by the batch's second pass on the garage meter; its flag was cleared. Nothing was billed again.`);
      setLinkBillOpen(false);
      await reload();
      fetchNotes(moveIdOf(move));
      setSnack({ open: true, message: "The move now shows its final bill.", severity: "success" });
    } catch (err) {
      setSnack({ open: true, message: err?.response?.data?.message || "Couldn't link the bill.", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const restoreMove = async () => {
    setSaving(true);
    try {
      await axios.post(`${basePath}/restore`);
      setRestoreOpen(false);
      await reload();
      setSnack({ open: true, message: "Move restored. It's back on the board as an open move.", severity: "success" });
    } catch (err) {
      setSnack({ open: true, message: err?.response?.data?.message || "Couldn't restore the move.", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  // The reason is saved as a note first, so a cleared flag always leaves an audit trail.
  const clearFlag = async () => {
    const reason = clearFlagReason.trim();
    if (!reason) {
      setClearFlagError("Enter why this move can go forward.");
      return;
    }
    setSaving(true);
    try {
      const flaggedFor = prediction?.reason ? ` (flagged: ${prediction.reason})` : "";
      await saveNote(`Flag cleared${flaggedFor}. Reason: ${reason}`);
      await axios.put(basePath, { issues: 0 });
      setClearFlagOpen(false);
      await reload();
      fetchNotes(moveIdOf(move));
      setSnack({ open: true, message: "Flag cleared and your reason was saved as a note.", severity: "success" });
    } catch (err) {
      setClearFlagError(err?.response?.data?.message || "Couldn't clear the flag.");
    } finally {
      setSaving(false);
    }
  };

  const patchMove = async (fields) => {
    setSaving(true);
    try {
      const response = await axios.put(basePath, fields);
      await reload();
      const { estimateRebuilt, estimateMessage, readMessage } = response.data || {};
      if (readMessage) setSnack({ open: true, message: `Move updated, but the garage read wasn't saved: ${readMessage}`, severity: "warning" });
      else if (estimateRebuilt) setSnack({ open: true, message: "Move updated, and the estimate was rebuilt with the new read and date.", severity: "success" });
      else if (estimateMessage) setSnack({ open: true, message: `Move updated, but the estimate couldn't be rebuilt: ${estimateMessage}`, severity: "warning" });
      else setSnack({ open: true, message: "Move updated.", severity: "success" });
      return true;
    } catch (err) {
      setSnack({ open: true, message: err?.response?.data?.message || "Failed to update move.", severity: "error" });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = () => {
    const updates = {};
    const origRead = move.meterRead != null ? String(move.meterRead) : "";
    const origDate = move.moveDate ? moment(move.moveDate, "YYYY-MM-DD").format("YYYY-MM-DD") : "";
    const origType = move.readAmtType || "";

    if (editMeterRead !== origRead && editMeterRead !== "") updates.meterRead = Number(editMeterRead);
    if (editMoveDate !== origDate && editMoveDate !== "") updates[dateField] = editMoveDate;
    if (editReadAmtType !== origType && editReadAmtType !== "") updates.readAmtType = editReadAmtType;
    const otherMeterReads = otherUnitMeters
      .filter((m) => !readOnMoveDate(m.meterId, false))
      .filter((m) => {
        const provided = readOnMoveDate(m.meterId, true);
        return (editOtherReads[m.meterId] ?? "") !== (provided ? String(provided.readAmount) : "");
      })
      .map((m) => ({ meterId: m.meterId, readAmount: editOtherReads[m.meterId] ?? "" }));
    if (otherMeterReads.length) updates.otherMeterReads = otherMeterReads;
    (CUSTOMER_FIELDS[kind] || []).forEach((name) => {
      const before = move[name] == null ? "" : String(move[name]);
      if ((editCustomer[name] ?? "").trim() !== before.trim()) updates[name] = (editCustomer[name] ?? "").trim();
    });
    if (kind === "in") {
      if (editCustomer.billDelivery && editCustomer.billDelivery !== billDeliveryOf(move)) Object.assign(updates, BILL_DELIVERY_FLAGS[editCustomer.billDelivery]);
      if ((editCustomer.ssn || "").trim()) updates.ssn = editCustomer.ssn.trim();
      if (editUnit && editUnit.unitUuid !== move.unitUuid) updates.unitUuid = editUnit.unitUuid;
    }

    if (Object.keys(updates).length === 0) {
      setEditing(false);
      return;
    }
    patchMove(updates).then((ok) => { if (ok) setEditing(false); });
  };

  const queueMove = async () => {
    setSaving(true);
    try {
      const response = await axios.post(`${basePath}/prepare-billing`);
      applyDetail(response.data);
      const rollovers = response.data?.rollovers || [];
      const rolloverNote = rollovers.map((r) =>
        ` Corrected a meter rollover (${Number(r.prevReadAmt).toLocaleString()} to ${Number(r.currReadAmt).toLocaleString()}): ${Number(r.usage).toLocaleString()} kWh.`
      ).join("");
      setSnack({ open: true, message: `Estimate built. Review the final bill before processing.${rolloverNote}`, severity: "success" });
    } catch (err) {
      setSnack({ open: true, message: err?.response?.data?.message || "Failed to prepare billing.", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const unqueueMove = async () => {
    setSaving(true);
    try {
      const response = await axios.post(`${basePath}/unqueue`);
      applyDetail(response.data);
      setSnack({ open: true, message: "Move unqueued. You can edit it and queue it again.", severity: "info" });
    } catch (err) {
      setSnack({ open: true, message: err?.response?.data?.message || "Failed to unqueue move.", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const processMove = async (withBill = true) => {
    setProcessDialogOpen(false);
    setProcessNoBillDialogOpen(false);
    const path = withBill ? `${basePath}/execute-billing` : `${basePath}/process?withBill=false`;
    setSaving(true);
    try {
      const response = await axios.post(path);
      const detail = response.data?.detail;
      if (detail) applyDetail(detail);
      else await reload();
      const moveResult = detail?.move || null;
      setSuccessInfo({
        accountNum: moveResult?.accountNum || null,
        accountUuid: moveResult?.accountUuid || null,
        withBill,
        billingStatus: response.data?.billing?.status || null,
        newCharges: billPreview?.newCharges ?? null,
      });
    } catch (err) {
      setSnack({ open: true, message: err?.response?.data?.message || "Failed to process move.", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const deleteMove = async () => {
    setDeleteDialogOpen(false);
    setDeleting(true);
    try {
      await axios.delete(basePath);
      // A duplicate is logged as one, with who deleted it, so Deleted moves says why it went.
      if (isDuplicate) await axios.put(`${basePath}/deletion`, { reason: "DUPLICATE" }).catch(() => {});
      setSnack({ open: true, message: "Move deleted.", severity: "success" });
      setTimeout(() => navigate("/move-ins-outs"), 600);
    } catch (err) {
      setSnack({ open: true, message: err?.response?.data?.message || "Failed to delete move.", severity: "error" });
    } finally {
      setDeleting(false);
    }
  };

  const openCreateMoveOut = () => {
    setLinkedMoveOutDate(move?.moveDate || "");
    setLinkedMoveOutOpen(true);
  };

  const createMoveOutForOccupant = async () => {
    setCreatingMoveOut(true);
    try {
      const resp = await axios.post(`/moves-dashboard/move-ins/${uuid}/create-move-out`, { moveOutDate: linkedMoveOutDate });
      setLinkedMoveOutOpen(false);
      const newUuid = resp.data?.moveOutUuid;
      setSnack({ open: true, message: "Move-out created.", severity: "success" });
      if (newUuid) setTimeout(() => navigate(`/move-ins-outs/out/${newUuid}`), 600);
    } catch (err) {
      setSnack({ open: true, message: err?.response?.data?.message || "Failed to create move-out.", severity: "error" });
    } finally {
      setCreatingMoveOut(false);
    }
  };

  const title = kind === "out" ? "Move-out" : "Move-in";

  if (!details) {
    return (
      <NavFrame page="Moves Dashboard">
        <StandardHeader title={title} />
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>
      </NavFrame>
    );
  }
  if (!canView) {
    return (
      <NavFrame page="Moves Dashboard">
        <StandardHeader title={title} />
        <Box sx={{ p: 3 }}><Typography variant="h6">You do not have privileges for the moves dashboard</Typography></Box>
      </NavFrame>
    );
  }
  if (!validKind) {
    return (
      <NavFrame page="Moves Dashboard">
        <StandardHeader title={title} />
        <Box sx={{ p: 3 }}><Alert severity="error">That move page does not exist.</Alert></Box>
      </NavFrame>
    );
  }

  const today = schedule.today || moment().format("YYYY-MM-DD");
  const isProcessed = move ? Number(move.processed) === 1 : false;
  const isDeleted = move ? Number(move.deleted) === 1 : false;
  // Another open move-in for the unit, or the tenant who already holds it: either way the move-in is deleted.
  const isDuplicate = prediction?.code === "DUPLICATE" || prediction?.code === "OCCUPANT_DUPLICATE";
  const hasIssues = move ? Number(move.issues) === 1 : false;
  const isOnHold = move ? Number(move.onHold) === 1 : false;
  const isReady = move ? Number(move.readyForProcess) === 1 : false;
  const hasBilling = bill && bill.available && bill.matched;
  const hasNegativeEstimate = hasBilling && !bill.singleBill && bill.usage != null && Number(bill.usage) < 0;
  const hasExcessiveUsage = hasBilling && !bill.singleBill && bill.usage != null && Number(bill.usage) > MAX_USAGE_KWH;
  const previewBlocked = Boolean(move && !isProcessed && billPreview && billPreview.blocked);
  // A move-in estimate that starts before a move-out's final read bills that period again (the account service's
  // billOverlap), so it has to be rebuilt; queueBlocked says why it can't be (re)built yet.
  const estimateOverlaps = prediction?.code === "ESTIMATE_OVERLAP" || prediction?.code === "MOVE_OUT_BILL_PENDING";
  const estimateStale = (previewBlocked && Boolean(billPreview.stale)) || (isReady && estimateOverlaps);
  const queueBlocked = prediction?.queueBlocked || null;
  const hasBillingIssue = hasNegativeEstimate || hasExcessiveUsage || previewBlocked;
  // A bill another way already covers (billing.coveredBy) can't be retried without billing those days twice, and one
  // billing has moved past (billing.billedPast) can't be billed at all.
  const billingStuck = isProcessed && billing && !billing.unlinkedBill && !billing.coveredBy && !billing.billedPast && (billing.status === "failed" || billing.status === "stalled" || billing.status === "not_queued");
  // Billing made the bill but didn't finish it (no PDF, work table or billing job left open).
  const billUnfinished = isProcessed && billing && billing.status === "billed" && billing.billCount === 1 && (billing.unfinished || []).length > 0;

  const period = move ? billPeriod({ bill, meterReads, moveDate: move.moveDate, isProcessed }) : null;
  const checks = move && !isProcessed && !isDeleted ? buildBatchChecks({ kind, move, prediction, bill, period, today, nextRun: schedule.nextRun, providedOtherReads }) : [];

  const deletedText = (() => {
    if (!isDeleted || !deletion) return null;
    const at = formatDateTime(deletion.at);
    const who = deletion.logged ? `Deleted ${at} by ${deletion.by}` : `Deleted ${at} · who deleted it wasn't recorded`;
    if (deletion.reason === "STALE") return `${who} · created more than 2 months ago and never processed (restore it to keep it)`;
    if (deletion.reason !== "DUPLICATE") return who;
    return `${who} · a duplicate${deletion.kept ? ` of ${deletion.kept.name || "another move-in"}'s move-in, which was kept (${deletion.kept.status})` : ""}`;
  })();

  const verdict = (() => {
    if (isDeleted) return { outcome: "deleted", label: "Deleted", reason: deletedText };
    if (prediction) {
      return {
        outcome: prediction.outcome,
        label: prediction.label,
        reason: prediction.reason,
        busy: isProcessed && (billing?.status === "queued" || Boolean(billing?.finishing)),
      };
    }
    if (isProcessed) return { outcome: "processed", label: "Processed", reason: null };
    if (hasIssues) return { outcome: "attention", label: "Flagged", reason: null };
    if (isOnHold) return { outcome: "hold", label: "On hold", reason: null };
    return { outcome: "upcoming", label: "Open", reason: null };
  })();

  const button = (key, label, onClick, buttonProps = {}) => (
    <Button key={key} onClick={onClick} disabled={saving || buttonProps.disabled} sx={{ textTransform: "none", bgcolor: buttonProps.variant === "outlined" ? "background.paper" : undefined }} {...buttonProps}>
      {label}
    </Button>
  );
  const editButton = button("edit", "Edit", () => setEditing(true), { variant: "outlined", startIcon: <EditIcon /> });
  // Queueing builds the estimate; a disabled button says why it has to wait.
  const queueButton = (key, label) => (queueBlocked
    ? <Tooltip key={key} title={queueBlocked} arrow><span>{button(key, label, queueMove, { variant: "contained", startIcon: <SendIcon />, disabled: true })}</span></Tooltip>
    : button(key, label, queueMove, { variant: "contained", startIcon: <SendIcon /> }));
  const menuItems = [];
  const actions = [];
  // The batch bills this move in its next run, so it can't be processed without a bill; hold it to look closer.
  const billsTonight = Boolean(prediction?.billsTonight);
  const runWhen = nextRunText(schedule.nextRun);
  // Billing's rule (BLPLS-407): no processing without a bill while the period before the move isn't billed.
  const noBillBlocked = prediction?.noBillBlocked || null;
  const noBillItem = billsTonight
    ? { key: "nobill", label: "Process without bill", hint: `It bills ${runWhen === "tonight" ? "tonight" : `in the ${runWhen} run`}. Put it on hold to look closer.`, icon: <FlashOnIcon fontSize="small" />, disabled: true, onClick: () => {} }
    : noBillBlocked
      ? { key: "nobill", label: "Process without bill", hint: noBillBlocked, icon: <FlashOnIcon fontSize="small" />, disabled: true, onClick: () => {} }
      : { key: "nobill", label: "Process without bill", icon: <FlashOnIcon fontSize="small" />, onClick: () => setProcessNoBillDialogOpen(true) };
  if (move && isDeleted) {
    actions.push(button("restore", "Restore…", () => setRestoreOpen(true), { variant: "contained", startIcon: <UndoIcon /> }));
  } else if (move && isProcessed) {
    if (billingStuck) actions.push(button("retry", "Retry billing", retryBilling, { variant: "contained", color: "error" }));
    if (billing?.unlinkedBill) actions.push(button("link-bill", "Link the bill…", () => setLinkBillOpen(true), { variant: "contained", color: "error" }));
    if (billUnfinished) actions.push(button("finish", "Finish bill…", () => setFinishBillOpen(true), { variant: "contained", color: "error" }));
  } else if (move && prediction?.code === "DUPLICATE") {
    actions.push(button("delete-dup", "Delete duplicate", () => setDeleteDialogOpen(true), { variant: "contained", color: "error", startIcon: <DeleteOutlined /> }));
    actions.push(editButton);
    menuItems.push({ key: "queue", label: "Queue for processing", icon: <SendIcon fontSize="small" />, onClick: queueMove });
    menuItems.push(noBillItem);
  } else if (move && prediction?.code === "OCCUPANT_DUPLICATE") {
    // The tenant moving in already holds the unit. If it isn't the same person after all, their move-out comes first.
    actions.push(button("delete-dup", "Delete duplicate", () => setDeleteDialogOpen(true), { variant: "contained", color: "error", startIcon: <DeleteOutlined /> }));
    actions.push(editButton);
    menuItems.push({ key: "create-out", label: "Not a duplicate: create the tenant's move-out", icon: <SendIcon fontSize="small" />, onClick: openCreateMoveOut });
    if (hasIssues) menuItems.push({ key: "clear", label: "Clear flag…", icon: <UndoIcon fontSize="small" />, onClick: () => { setClearFlagReason(""); setClearFlagError(""); setClearFlagOpen(true); } });
  } else if (move && prediction?.code === "UNIT_OCCUPIED" && !hasIssues && !isOnHold) {
    // The tenant's move-out has to run first; queueing this would final-bill a tenant who isn't leaving.
    const moveOut = occupantMoveOuts[0];
    actions.push(moveOut
      ? <Button key="open-out" component={RouterLink} to={`/move-ins-outs/out/${moveOut.uuid}`} variant="contained" sx={{ textTransform: "none" }}>Open the tenant's move-out</Button>
      : button("create-out", "Create the tenant's move-out", openCreateMoveOut, { variant: "contained", disabled: creatingMoveOut }));
    actions.push(editButton);
    if (isReady) menuItems.push({ key: "unqueue", label: "Unqueue", icon: <UndoIcon fontSize="small" />, onClick: unqueueMove });
    else menuItems.push({ key: "queue", label: "Queue for processing anyway", icon: <SendIcon fontSize="small" />, onClick: queueMove });
    menuItems.push(noBillItem);
  } else if (move && hasIssues) {
    actions.push(button("clear", "Clear flag…", () => { setClearFlagReason(""); setClearFlagError(""); setClearFlagOpen(true); }, { variant: "contained", color: "error" }));
    actions.push(editButton);
  } else if (move && isOnHold) {
    actions.push(button("release", "Release hold", () => patchMove({ onHold: 0 }), { variant: "contained", color: "warning" }));
    actions.push(editButton);
  } else if (move && isReady && estimateStale) {
    actions.push(queueButton("rebuild", "Rebuild estimate"));
    actions.push(button("unqueue", "Unqueue", unqueueMove, { variant: "outlined", startIcon: <UndoIcon /> }));
    actions.push(editButton);
  } else if (move && isReady) {
    const label = hasNegativeEstimate ? "Negative estimate"
      : hasExcessiveUsage ? `Over ${MAX_USAGE_KWH.toLocaleString()} kWh`
        : previewBlocked ? "Billing blocked"
          : billPreview?.newCharges != null ? `Process with bill · ${money(billPreview.newCharges)}` : "Process with bill";
    actions.push(button("process", label, () => setProcessDialogOpen(true), { variant: "contained", color: hasBillingIssue ? "error" : "success", startIcon: <FlashOnIcon />, disabled: hasBillingIssue }));
    actions.push(button("unqueue", "Unqueue", unqueueMove, { variant: "outlined", startIcon: <UndoIcon /> }));
    actions.push(editButton);
    menuItems.push(noBillItem);
  } else if (move) {
    actions.push(queueButton("queue", "Queue for processing"));
    if (billsTonight || noBillBlocked) menuItems.push(noBillItem);
    else actions.push(button("nobill", "Process without bill", () => setProcessNoBillDialogOpen(true), { variant: "outlined" }));
    actions.push(editButton);
  }
  if (move && !isProcessed && !isDeleted) {
    if (!isOnHold && !hasIssues) menuItems.push({ key: "hold", label: "Put on hold", icon: <PauseCircleOutlineIcon fontSize="small" />, onClick: () => patchMove({ onHold: 1 }) });
    if (!isDuplicate) {
      menuItems.push({ key: "delete", label: "Delete move", icon: <DeleteOutlined fontSize="small" color="error" />, onClick: () => setDeleteDialogOpen(true), danger: true });
    }
  }
  if (menuItems.length) {
    actions.push(
      <IconButton key="more" aria-label="More actions" onClick={(event) => setMenuAnchor(event.currentTarget)} disabled={saving || deleting} size="small" sx={{ bgcolor: "background.paper", border: 1, borderColor: "divider" }}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
    );
  }

  const state = move ? recordState(move) : null;
  const created = move ? creatorText(kind, move.createdByName, move.createdBy) : "";
  const processedBy = move ? (move.processedByName || (move.processedBy ? `User #${move.processedBy}` : "the batch")) : null;

  return (
    <NavFrame page="Moves Dashboard">
      <StandardHeader title={move?.name || title} appPath="Moves Dashboard" />

      <Snackbar open={snack.open} autoHideDuration={5000} onClose={() => setSnack((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))} variant="filled" sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>

      <Box sx={{ maxWidth: 1320, margin: "0 auto", width: "100%", px: { xs: 1.5, md: 3 }, pb: 3 }}>
        <Button component={RouterLink} to="/move-ins-outs" startIcon={<ArrowBackIcon />} size="small" sx={{ mb: 1, textTransform: "none" }}>
          Moves dashboard
        </Button>

        {loading ? <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box> : null}
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

        {move ? (
          <>
            {/* ── What happens next, and the action that decides it ── */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 1.5, mb: 1.5 }}>
                <Box sx={{ flex: "1 1 420px", minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                    <Box component="span" sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, color: "#fff", bgcolor: TYPE_COLOR[kind], px: 1, py: 0.25, borderRadius: 1 }}>
                      {kind === "in" ? "MOVE-IN" : "MOVE-OUT"}
                    </Box>
                    <Typography sx={{ fontSize: 18, fontWeight: 600 }}>{move.name || "Unnamed move"}</Typography>
                    <Typography sx={{ fontSize: 15, color: "text.secondary" }}>
                      <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>{formatDate(move.moveDate)}</Box> · {relativeDay(move.moveDate, today)}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 13.5, mt: 0.5 }}>
                    {propertyHref(move) ? <Link href={propertyHref(move)} target="_blank" rel="noopener noreferrer">{move.propertyName}</Link> : (move.propertyName || "No property")}
                    {" · "}
                    {unitHref(move) ? <Link href={unitHref(move)} target="_blank" rel="noopener noreferrer">{move.unitAddress}</Link> : (move.unitAddress || "No unit linked")}
                    {move.pmName ? ` · ${move.pmName}` : ""}
                    {" · "}
                    {accountHref(move)
                      ? <Link href={accountHref(move)} target="_blank" rel="noopener noreferrer">Acct #{move.accountNum}</Link>
                      : kind === "in" ? "Account opens when processed" : "No account"}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 0.25 }}>
                    {move.sourceLabel ? <><strong>{move.sourceLabel}</strong> · </> : null}
                    Created {formatDateTime(move.created)}{created ? ` ${created}` : ""}
                    {isProcessed ? ` · Processed ${formatDateTime(move.dateProcessed)} by ${processedBy}` : ""}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                  <Chip size="small" label={state.label} color={state.color} variant={isProcessed ? "filled" : "outlined"} />
                  {Number(move.finalBill) === 1 && !isProcessed ? <Chip size="small" label="Final bill" variant="outlined" /> : null}
                  {Number(move.accountDisconnected) === 1 ? <Chip size="small" label="Account disconnected" color="error" variant="outlined" /> : null}
                </Box>
              </Box>

              <VerdictBar
                verdict={verdict}
                actions={actions.length ? actions : null}
                extra={isDeleted && deletion?.kept ? (
                  <Link component={RouterLink} to={`/move-ins-outs/in/${deletion.kept.uuid}`} sx={{ fontSize: 13 }}>Open the kept move-in →</Link>
                ) : null}
              />
              {checks.length ? <ChecksStrip checks={checks} /> : null}
            </Paper>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 7fr) minmax(0, 5fr)" }, columnGap: 2, alignItems: "start" }}>
              <Box>
                <BillPanel
                  kind={kind}
                  move={move}
                  bill={bill}
                  billPreview={billPreview}
                  moveBill={moveBill}
                  billing={billing}
                  billingTarget={billingTarget}
                  prediction={prediction}
                  period={period}
                  isProcessed={isProcessed}
                >
                  {move.moveDate ? (
                    <UnitTimeline
                      kind={kind}
                      moveDate={move.moveDate}
                      outcome={verdict.outcome}
                      unitMoves={unitMoves}
                      reads={meterReads}
                      otherMeters={otherMeters}
                      period={period}
                      today={today}
                    />
                  ) : null}
                </BillPanel>
              </Box>

              <Box>
                <UnitPanel
                  kind={kind}
                  move={move}
                  unitMoves={unitMoves}
                  occupantMoveOuts={occupantMoveOuts}
                  unitLastBill={unitLastBill}
                  handoff={handoff}
                  currentOccupant={currentOccupant}
                  prediction={prediction}
                  today={today}
                  onCreateMoveOut={openCreateMoveOut}
                  creatingMoveOut={creatingMoveOut}
                  unitHref={unitHref(move)}
                  unitMeters={unitMeters}
                  likelyDuplicate={prediction?.code === "OCCUPANT_DUPLICATE"}
                />

                <Panel title={kind === "in" ? "New customer" : "Customer"}>
                  {emailMatches.length > 0 && !isProcessed ? (
                    <Alert severity={hasPortalUser ? "warning" : "info"} icon={<LinkIcon fontSize="small" />} sx={{ mb: 1.25, py: 0.25, "& .MuiAlert-message": { width: "100%" } }}>
                      <Typography sx={{ fontSize: 12.5 }}>
                        {emailMatches.length} existing account{emailMatches.length > 1 ? "s use" : " uses"} this email
                        {hasPortalUser ? ". They have a portal login, and the new account will be linked to it when processed." : "."}
                      </Typography>
                      {emailMatches.map((match) => (
                        <Typography key={match.accountNum} sx={{ fontSize: 12.5 }}>
                          {match.uuid
                            ? <Link component={RouterLink} to={`/accountmanager/${match.accountNum}/${match.uuid}`} target="_blank">#{match.accountNum}</Link>
                            : `#${match.accountNum}`}
                          {` ${[match.firstName, match.lastName].filter(Boolean).join(" ")}`}
                          {match.customerType ? ` (${match.customerType})` : ""}
                          {match.propertyName ? ` · ${match.propertyName}${match.unitAddress ? `, ${match.unitAddress}` : ""}` : ""}
                        </Typography>
                      ))}
                    </Alert>
                  ) : null}
                  <KeyValues
                    rows={[
                      ["Email", move.email],
                      ["Phone", kind === "in" ? [move.phone1, move.phone2].filter(Boolean).join(" · ") : move.phone],
                      kind === "in"
                        ? ["Billing address", joinAddress([move.billingAddress1, move.billingAddress2, move.billingCity, move.billingState, move.billingZip])]
                        : ["Forwarding", joinAddress([move.fwdAddress1, move.fwdAddress2, move.fwdCity, move.fwdState, move.fwdZip])],
                      kind === "out" ? ["Service ZIP", move.serviceZipCode] : null,
                      kind === "in" ? ["Lease end", formatDate(move.leaseEndDate)] : null,
                      kind === "in" && move.customerType ? ["Customer type", move.customerType] : null,
                      ["Move read", move.meterRead == null ? null : `${Number(move.meterRead).toLocaleString()}${move.readAmtType ? ` (${move.readAmtType})` : ""}${Number(move.meterRead) === 0 ? " · estimate" : ""}`],
                      move.pmName ? ["Property manager", [move.pmName, move.pmContactName, move.pmPhone, move.pmEmail].filter(Boolean).join(" · ")] : null,
                      ["Updated", formatDateTime(move.updated)],
                    ]}
                  />
                </Panel>

                <NotesPanel notes={notes} loading={loadingNotes} onAdd={addNote} formatDateTime={formatDateTime} />
              </Box>
            </Box>

            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
              {menuItems.map((item) => (
                <MenuItem key={item.key} disabled={Boolean(item.disabled)} onClick={() => { setMenuAnchor(null); item.onClick(); }} sx={item.danger ? { color: "error.main" } : undefined}>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <Box>
                    {item.label}
                    {item.hint ? <Typography sx={{ fontSize: 11.5, color: "text.secondary", whiteSpace: "normal", maxWidth: 240 }}>{item.hint}</Typography> : null}
                  </Box>
                </MenuItem>
              ))}
            </Menu>

            <Dialog open={editing} onClose={() => setEditing(false)} maxWidth="md" fullWidth>
              <DialogTitle sx={{ fontWeight: "bold" }}>Edit move</DialogTitle>
              <DialogContent>
                <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "text.secondary", mt: 1 }}>Move</Typography>
                <Box sx={{ display: "grid", gap: 2, mt: 1, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
                  <TextField label={dateLabel} type="date" value={editMoveDate} onChange={(e) => setEditMoveDate(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
                  <TextField label={`${otherUnitMeters.length ? "Main meter" : "Meter"}${unitMeters[0] ? ` #${unitMeters[0].meterNum}` : ""} read (kWh)`} type="number" value={editMeterRead} onChange={(e) => setEditMeterRead(e.target.value)} inputProps={{ min: 0 }} fullWidth />
                  <TextField label="Read type" select value={editReadAmtType} onChange={(e) => setEditReadAmtType(e.target.value)} fullWidth>
                    <MenuItem value="">Not set</MenuItem>
                    <MenuItem value="ACT">Actual (ACT)</MenuItem>
                    <MenuItem value="EST">Estimate (EST)</MenuItem>
                  </TextField>
                  {otherUnitMeters.map((m) => {
                    const imported = readOnMoveDate(m.meterId, false);
                    const provided = readOnMoveDate(m.meterId, true);
                    const latest = otherMeters.find((o) => String(o.meterNum) === String(m.meterNum));
                    return (
                      <TextField
                        key={m.meterId}
                        label={`${Number(m.meterCode) === 2 ? "Garage" : "Second"} meter #${m.meterNum} read (kWh)`}
                        type="number"
                        value={imported ? String(imported.readAmount) : editOtherReads[m.meterId] ?? ""}
                        disabled={Boolean(imported)}
                        onChange={(e) => setEditOtherReads((prev) => ({ ...prev, [m.meterId]: e.target.value }))}
                        inputProps={{ min: 0 }}
                        fullWidth
                        helperText={imported
                          ? "From the meter readings on the move date. The final bill uses it."
                          : provided
                            ? "Provided for this move, an actual read on the move date. Clear it to estimate instead."
                            : `${latest ? `Last read ${moment(String(latest.readAt).slice(0, 10), "YYYY-MM-DD").format("MMM D")}: ${Number(latest.readAmount).toLocaleString()}. ` : ""}Leave blank to estimate it.`}
                      />
                    );
                  })}
                </Box>

                {kind === "in" ? (
                  <>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "text.secondary", mt: 2.5 }}>Unit</Typography>
                    <Autocomplete
                      size="small"
                      sx={{ mt: 1 }}
                      options={unitSearch.options}
                      loading={unitSearch.loading}
                      value={editUnit}
                      filterOptions={(x) => x}
                      isOptionEqualToValue={(a, b) => a.unitUuid === b.unitUuid}
                      getOptionLabel={(u) => `${u.unitAddress || "—"} · ${u.propertyName || ""}`}
                      onInputChange={(e, value, reason) => { if (reason === "input") setUnitQuery(value); }}
                      onChange={(e, value) => setEditUnit(value)}
                      noOptionsText={unitQuery.trim().length < 2 ? "Type an address, property, or occupant" : "No units match"}
                      renderOption={(props, u) => (
                        <li {...props} key={u.unitUuid}>
                          <Box>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>{u.unitAddress}</Typography>
                            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                              {u.propertyName}{u.occupantName ? ` · ${u.occupantName} (${u.occupantCustomerType || "occupant"})` : " · no occupant"}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      renderInput={(params) => <TextField {...params} label="Unit" placeholder="Address, property, or current occupant" />}
                    />
                    {editUnit && editUnit.unitUuid !== move.unitUuid ? (
                      <Alert severity="info" sx={{ mt: 1, py: 0 }}>
                        The service address moves to the new unit{isReady ? ", and the estimate is rebuilt for its occupant" : ""}.
                        Garage reads entered for the old unit are removed.
                      </Alert>
                    ) : null}
                  </>
                ) : null}

                <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "text.secondary", mt: 2.5 }}>Customer</Typography>
                <Box sx={{ display: "grid", gap: 2, mt: 1, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
                  {(kind === "in"
                    ? [["firstName", "First name"], ["lastName", "Last name"], ["email", "Email"], ["phone1", "Phone"], ["phone2", "Second phone (optional)"]]
                    : [["email", "Email"], ["phone", "Phone"]]
                  ).map(([name, label]) => (
                    <TextField key={name} size="small" label={label} value={editCustomer[name] ?? ""} onChange={(e) => setEditCustomer((prev) => ({ ...prev, [name]: e.target.value }))} fullWidth />
                  ))}
                  {kind === "in" ? (
                    <>
                      <TextField
                        size="small"
                        label="SSN"
                        value={editCustomer.ssn ?? ""}
                        onChange={(e) => setEditCustomer((prev) => ({ ...prev, ssn: e.target.value }))}
                        inputProps={{ maxLength: 11, autoComplete: "off" }}
                        placeholder={move.last4SSN ? `•••-••-${move.last4SSN}` : ""}
                        helperText={move.last4SSN ? `On file: ending ${move.last4SSN}. Enter a new one to replace it.` : "Optional. Only the last 4 show after it's saved."}
                        fullWidth
                      />
                      <TextField size="small" select label="Bills sent by" value={editCustomer.billDelivery || "email"} onChange={(e) => setEditCustomer((prev) => ({ ...prev, billDelivery: e.target.value }))} fullWidth>
                        <MenuItem value="email">Email</MenuItem>
                        <MenuItem value="paper">Paper (mailed)</MenuItem>
                        <MenuItem value="both">Email and paper</MenuItem>
                      </TextField>
                    </>
                  ) : null}
                </Box>
                <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 2 }}>{kind === "in" ? "Billing address" : "Forwarding address"}</Typography>
                <Box sx={{ display: "grid", gap: 2, mt: 1, gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr" } }}>
                  {(kind === "in"
                    ? [["billingAddress1", "Address"], ["billingAddress2", "Apt / suite (optional)"]]
                    : [["fwdAddress1", "Address"], ["fwdAddress2", "Apt / suite (optional)"]]
                  ).map(([name, label]) => (
                    <TextField key={name} size="small" label={label} value={editCustomer[name] ?? ""} onChange={(e) => setEditCustomer((prev) => ({ ...prev, [name]: e.target.value }))} fullWidth />
                  ))}
                </Box>
                <Box sx={{ display: "grid", gap: 2, mt: 2, gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr 1fr" } }}>
                  {(kind === "in"
                    ? [["billingCity", "City"], ["billingState", "State"], ["billingZip", "ZIP"]]
                    : [["fwdCity", "City"], ["fwdState", "State"], ["fwdZip", "ZIP"]]
                  ).map(([name, label]) => (
                    <TextField key={name} size="small" label={label} value={editCustomer[name] ?? ""} onChange={(e) => setEditCustomer((prev) => ({ ...prev, [name]: e.target.value }))} fullWidth />
                  ))}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button disabled={saving} onClick={() => setEditing(false)}>Cancel</Button>
                <Button variant="contained" disabled={saving} onClick={handleSaveEdit}>{saving ? "Saving..." : "Save"}</Button>
              </DialogActions>
            </Dialog>

            <Dialog open={processDialogOpen} onClose={() => setProcessDialogOpen(false)} maxWidth="xs" fullWidth>
              <DialogTitle sx={{ fontWeight: "bold" }}>Process with bill</DialogTitle>
              <DialogContent>
                <Typography>
                  Process this {kind === "out" ? "move-out" : "move-in"} for <strong>{move.name || "this customer"}</strong> and create the final bill?
                </Typography>
                {billPreview && billPreview.newCharges != null ? (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Final bill: <strong>{money(billPreview.newCharges)}</strong> in new charges
                    {billingTarget ? <> to {billingTarget.name || `Acct #${billingTarget.accountNum}`}</> : null}
                    {hasBilling && bill.usage != null ? ` (${Number(bill.usage).toLocaleString()} kWh)` : ""}.
                  </Alert>
                ) : null}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setProcessDialogOpen(false)}>Cancel</Button>
                <Button variant="contained" color="success" onClick={() => processMove(true)} disabled={saving}>
                  {saving ? "Processing..." : "Process with bill"}
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog open={processNoBillDialogOpen} onClose={() => setProcessNoBillDialogOpen(false)} maxWidth="xs" fullWidth>
              <DialogTitle sx={{ fontWeight: "bold" }}>Process without bill</DialogTitle>
              <DialogContent>
                <Typography>
                  Process this {kind === "out" ? "move-out" : "move-in"} for <strong>{move.name || "this customer"}</strong> without a final bill?
                </Typography>
                <Alert severity="warning" sx={{ mt: 2 }}>No bill will be created. This can't be undone.</Alert>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setProcessNoBillDialogOpen(false)}>Cancel</Button>
                <Button variant="contained" color="warning" onClick={() => processMove(false)} disabled={saving}>
                  {saving ? "Processing..." : "Process without bill"}
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog open={linkedMoveOutOpen} onClose={() => setLinkedMoveOutOpen(false)} maxWidth="xs" fullWidth>
              <DialogTitle sx={{ fontWeight: "bold" }}>Create the tenant's move-out</DialogTitle>
              <DialogContent>
                <Typography sx={{ mb: 2 }}>
                  A move-out for <strong>{move.occupantName || "the current tenant"}</strong>, who still occupies the unit.
                </Typography>
                <TextField
                  label="Move-out date"
                  type="date"
                  value={linkedMoveOutDate}
                  onChange={(e) => setLinkedMoveOutDate(e.target.value)}
                  inputProps={{ max: move.moveDate }}
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(linkedMoveOutDate && move.moveDate && linkedMoveOutDate > move.moveDate)}
                  helperText={`On or before this move-in (${formatDate(move.moveDate)}). A later date leaves the tenant on the unit, and the batch flags this move-in.`}
                  fullWidth
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setLinkedMoveOutOpen(false)}>Cancel</Button>
                <Button
                  variant="contained"
                  onClick={createMoveOutForOccupant}
                  disabled={creatingMoveOut || !linkedMoveOutDate || Boolean(move.moveDate && linkedMoveOutDate > move.moveDate)}
                >
                  {creatingMoveOut ? "Creating…" : "Create move-out"}
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog open={linkBillOpen} onClose={() => setLinkBillOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle sx={{ fontWeight: "bold" }}>Link the final bill</DialogTitle>
              {billing?.unlinkedBill ? (
                <DialogContent>
                  <Typography>
                    This move was billed when it was processed: <strong>{money(billing.unlinkedBill.newCharges)}</strong> in new charges
                    to {billing.unlinkedBill.name || "the account"} (#{billing.unlinkedBill.accountNum}), billed {moment(billing.unlinkedBill.billDate, "YYYY-MM-DD").format("MMM D, YYYY")}.
                  </Typography>
                  <Typography sx={{ mt: 1.5 }}>
                    The batch then ran it again for the unit's garage meter, pointed the move at an empty estimate, and flagged it, so this page
                    couldn't find the bill. Linking points the move back at its bill and clears that flag.
                  </Typography>
                  <Alert severity="info" sx={{ mt: 2 }}>Nothing is billed again. A note records the change.</Alert>
                </DialogContent>
              ) : null}
              <DialogActions>
                <Button onClick={() => setLinkBillOpen(false)}>Cancel</Button>
                <Button variant="contained" onClick={linkBill} disabled={saving}>{saving ? "Linking..." : "Link the bill"}</Button>
              </DialogActions>
            </Dialog>

            <Dialog open={restoreOpen} onClose={() => setRestoreOpen(false)} maxWidth="xs" fullWidth>
              <DialogTitle sx={{ fontWeight: "bold" }}>Restore this move</DialogTitle>
              <DialogContent>
                <Typography>
                  Restore {move.name || "this"}'s {kind === "out" ? "move-out" : "move-in"}? It goes back on the moves dashboard as an open move.
                </Typography>
                {deletion?.kept && deletion.kept.status === "processed" ? (
                  <Alert severity="warning" sx={{ mt: 2 }}>{processedRivalText(deletion.kept)}</Alert>
                ) : null}
                {deletion?.kept && (deletion.kept.status === "open" || deletion.kept.status === "flagged" || deletion.kept.status === "on hold") ? (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {deletion.kept.name || "The kept move-in"} is still open on this unit. The batch keeps one open move-in per unit,
                    so its next run deletes one of them again unless you delete the other.
                  </Alert>
                ) : null}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setRestoreOpen(false)}>Cancel</Button>
                <Button variant="contained" onClick={restoreMove} disabled={saving}>{saving ? "Restoring..." : "Restore"}</Button>
              </DialogActions>
            </Dialog>

            <Dialog open={finishBillOpen} onClose={() => setFinishBillOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle sx={{ fontWeight: "bold" }}>Finish the final bill</DialogTitle>
              <DialogContent>
                <Typography>
                  Billing made this bill but stopped before finishing it: {(billing?.unfinished || []).join("; ")}.
                </Typography>
                <Typography sx={{ mt: 1.5 }}>
                  {billing?.billsWithoutPdf?.length
                    ? "Finishing makes the bill's PDF and emails it to the customer, as billing would have, then closes the work table and the billing job."
                    : "Finishing closes the work table and the billing job. The bill and its PDF are already done."}
                </Typography>
                <Alert severity="info" sx={{ mt: 2 }}>It doesn't make a new bill or change the amount.</Alert>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setFinishBillOpen(false)}>Cancel</Button>
                <Button variant="contained" color="error" onClick={finishBill} disabled={saving}>{saving ? "Finishing..." : "Finish bill"}</Button>
              </DialogActions>
            </Dialog>

            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
              <DialogTitle sx={{ fontWeight: "bold" }}>{isDuplicate ? "Delete duplicate" : "Delete move"}</DialogTitle>
              <DialogContent>
                <Typography>
                  Delete this {kind === "out" ? "move-out" : "move-in"} for <strong>{move.name || "this customer"}</strong>?
                  {isDuplicate ? " It's logged as a duplicate." : ""} You can restore it from Deleted moves.
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                <Button variant="contained" color="error" onClick={deleteMove} disabled={deleting}>{deleting ? "Deleting..." : "Delete"}</Button>
              </DialogActions>
            </Dialog>

            <Dialog open={clearFlagOpen} onClose={() => setClearFlagOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle sx={{ fontWeight: "bold" }}>Clear flag and move forward</DialogTitle>
              <DialogContent>
                {prediction?.reason ? <Alert severity="warning" sx={{ mb: 2 }}>{prediction.reason}</Alert> : null}
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Clearing the flag lets you queue and process this move. If the problem is still there, the batch's next run will flag it again.
                </Typography>
                <TextField
                  autoFocus
                  fullWidth
                  multiline
                  minRows={2}
                  label="Why can this move go forward?"
                  value={clearFlagReason}
                  onChange={(event) => { setClearFlagReason(event.target.value); setClearFlagError(""); }}
                  error={Boolean(clearFlagError)}
                  helperText={clearFlagError || "Saved as a note on this move."}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setClearFlagOpen(false)}>Cancel</Button>
                <Button variant="contained" color="error" onClick={clearFlag} disabled={saving}>Clear flag</Button>
              </DialogActions>
            </Dialog>

            <Dialog open={Boolean(successInfo)} onClose={() => setSuccessInfo(null)} maxWidth="xs" fullWidth>
              <DialogTitle sx={{ fontWeight: "bold", display: "flex", alignItems: "center", gap: 1 }}>
                <CheckCircleIcon color="success" /> Move processed
              </DialogTitle>
              <DialogContent>
                {successInfo ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
                    <Typography>
                      <strong>{move.name || "Customer"}</strong> has been {kind === "out" ? "moved out" : "moved in"}.
                    </Typography>
                    {successInfo.accountNum ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">Account:</Typography>
                        <Chip label={`#${successInfo.accountNum}`} size="small" color="primary" component={RouterLink} to={`/accountmanager/${successInfo.accountNum}/${successInfo.accountUuid || ""}`} clickable />
                      </Box>
                    ) : null}
                    {successInfo.withBill && (successInfo.billingStatus === "queued" || successInfo.billingStatus === "billed") ? (
                      <Alert severity="info">
                        The final bill is queued
                        {successInfo.newCharges != null ? <> for <strong>{money(successInfo.newCharges)}</strong> in new charges</> : null}.
                        This page shows it when billing finishes.
                      </Alert>
                    ) : null}
                    {successInfo.withBill && successInfo.billingStatus !== "queued" && successInfo.billingStatus !== "billed" ? (
                      <Alert severity="error">The move was processed, but billing didn't start. Use Retry billing on this page.</Alert>
                    ) : null}
                    {!successInfo.withBill ? <Alert severity="info">Processed without a final bill.</Alert> : null}
                  </Box>
                ) : null}
              </DialogContent>
              <DialogActions>
                {successInfo?.accountNum && successInfo?.accountUuid ? (
                  <Button component={RouterLink} to={`/accountmanager/${successInfo.accountNum}/${successInfo.accountUuid}`} color="primary">Go to account</Button>
                ) : null}
                <Button variant="contained" onClick={() => setSuccessInfo(null)}>Done</Button>
              </DialogActions>
            </Dialog>
          </>
        ) : null}
      </Box>
    </NavFrame>
  );
}

function mapStateToProps(state) {
  return { user: state.user };
}

export default connect(mapStateToProps)(MoveDetailPage);
